import { useState, type FormEvent, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher'
import { PasswordInput } from '@/components/ui/PasswordInput'
import { apiError } from '@/pages/settings/datacenter/shared'
import { portalApi, setPortalToken } from './portal-api'

export function PortalCard({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="glass-card w-full max-w-md p-8">
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  )
}

/** Pusula Portal girişi — lisans sahibi ya da eski bulut kullanıcısı (aynı e-posta/şifre) */
export function PortalLoginPage(): JSX.Element {
  const { t } = useTranslation('portal')
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const res = await portalApi.post<{ data: { token: string } }>('/portal/auth/login', { email, password, remember }, { withCredentials: true })
      setPortalToken(res.data.data.token)
      navigate('/portal')
    } catch (err) {
      setError(apiError(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <PortalCard title={t('login.title')} subtitle={t('login.subtitle')}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <div><label className="label" htmlFor="p-email">{t('login.email')}</label><input id="p-email" type="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        <div><label className="label" htmlFor="p-pass">{t('login.password')}</label><PasswordInput id="p-pass" required value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />{t('login.remember')}</label>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button type="submit" className="btn-primary w-full" disabled={busy}>{t('login.submit')}</button>
      </form>
      <div className="mt-4 flex justify-between text-sm">
        <Link to="/forgot" className="text-primary hover:underline">{t('login.forgot')}</Link>
        <Link to="/signup" className="text-primary hover:underline">{t('login.signup')}</Link>
      </div>
    </PortalCard>
  )
}

/** Kayıt: deneme hesabı + 14 günlük deneme lisansı; şifre belirleme bağlantısı e-postayla gelir */
export function PortalSignupPage(): JSX.Element {
  const { t } = useTranslation('portal')
  const [form, setForm] = useState({ companyName: '', firstName: '', lastName: '', email: '' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const set = (key: keyof typeof form) => (e: { target: { value: string } }): void => setForm((f) => ({ ...f, [key]: e.target.value }))
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await portalApi.post('/portal/auth/signup', form)
      setDone(true)
    } catch (err) {
      setError(apiError(err))
    } finally {
      setBusy(false)
    }
  }
  return (
    <PortalCard title={t('signup.title')} subtitle={done ? t('signup.sent') : t('signup.subtitle')}>
      {!done && (
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          <div><label className="label" htmlFor="su-company">{t('signup.company')}</label><input id="su-company" required className="input" value={form.companyName} onChange={set('companyName')} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label" htmlFor="su-first">{t('signup.firstName')}</label><input id="su-first" required className="input" value={form.firstName} onChange={set('firstName')} /></div>
            <div><label className="label" htmlFor="su-last">{t('signup.lastName')}</label><input id="su-last" required className="input" value={form.lastName} onChange={set('lastName')} /></div>
          </div>
          <div><label className="label" htmlFor="su-email">{t('login.email')}</label><input id="su-email" type="email" required className="input" value={form.email} onChange={set('email')} /></div>
          <p className="text-xs text-muted-foreground">{t('signup.note')}</p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <button type="submit" className="btn-primary w-full" disabled={busy}>{t('signup.submit')}</button>
        </form>
      )}
      <Link to="/login" className="mt-4 inline-block text-sm text-primary hover:underline">{t('forgot.back')}</Link>
    </PortalCard>
  )
}

export function PortalForgotPage(): JSX.Element {
  const { t } = useTranslation('portal')
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    await portalApi.post('/portal/auth/forgot', { email }).catch(() => undefined)
    setSent(true)
  }
  return (
    <PortalCard title={t('forgot.title')} subtitle={sent ? t('forgot.sent') : t('forgot.subtitle')}>
      {!sent && (
        <form className="space-y-4" onSubmit={(e) => void submit(e)}>
          <input type="email" required className="input" placeholder={t('login.email')} value={email} onChange={(e) => setEmail(e.target.value)} />
          <button type="submit" className="btn-primary w-full">{t('forgot.submit')}</button>
        </form>
      )}
      <Link to="/login" className="mt-4 inline-block text-sm text-primary hover:underline">{t('forgot.back')}</Link>
    </PortalCard>
  )
}

/** E-postadaki bağlantıyla şifre belirleme (yeni lisans sahibi) ya da sıfırlama */
export function PortalSetPasswordPage(): JSX.Element {
  const { t } = useTranslation('portal')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (password !== confirm) {
      setError(t('setPassword.mismatch'))
      return
    }
    try {
      const res = await portalApi.post<{ data: { token: string } }>('/portal/auth/set-password', { token: params.get('token') ?? '', password })
      setPortalToken(res.data.data.token)
      navigate('/portal')
    } catch (err) {
      setError(apiError(err))
    }
  }
  return (
    <PortalCard title={t('setPassword.title')} subtitle={t('setPassword.subtitle')}>
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <div><label className="label" htmlFor="sp-1">{t('setPassword.password')}</label><PasswordInput id="sp-1" required value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <div><label className="label" htmlFor="sp-2">{t('setPassword.confirm')}</label><PasswordInput id="sp-2" required value={confirm} onChange={(e) => setConfirm(e.target.value)} /></div>
        <p className="text-xs text-muted-foreground">{t('setPassword.policy')}</p>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <button type="submit" className="btn-primary w-full">{t('setPassword.submit')}</button>
      </form>
    </PortalCard>
  )
}
